/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Container } from "react-awesome-styled-grid";
import { useForm } from "react-hook-form";
import { When } from "react-if";
import { Button, Dropdown, Radio, Label, theme } from "@nordcloud/gnui";
import { Fieldset } from "~/components";
import { isEmpty, isNotEmpty } from "~/tools";
import { SelectAppEnvFormData } from "../../components/AppEnvSelector/types";
import { getEnvironmentNameNid } from "../../components/AppEnvSelector/utils";
import { ApplicationOption } from "../../components/types";

type SingleEnvironmentFormProps = {
  applications: ApplicationOption[];
  onSubmit: (data: SelectAppEnvFormData) => Promise<void>;
};

export function SingleEnvironmentForm({
  applications,
  onSubmit,
}: SingleEnvironmentFormProps) {
  const { register, handleSubmit, setValue, watch } = useForm<{
    applicationId: string;
    environmentId: string;
  }>();

  React.useEffect(() => {
    register("applicationId", { required: true });
    register("environmentId", { required: true });
  }, [register]);

  const [application, setApplication] = React.useState("");

  const { applicationId = "", environmentId = "" } = watch();

  const isDisabled = isEmpty(applicationId) || isEmpty(environmentId);

  function handleOptionChange(
    changeEvent: React.ChangeEvent<HTMLInputElement>
  ) {
    setValue("environmentId", changeEvent.target.value);
  }

  const appEnvs = applications
    .filter(({ nid }) => nid === applicationId)
    .flatMap((app) => app.environments)
    .map((env) => getEnvironmentNameNid(env));

  const onSelectApplication = (value: string) => {
    setApplication(value);
    setValue("applicationId", value);
    setValue("environmentId", "");
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Fieldset>
        <Label required name="Application" />
        <Dropdown
          name="Select Application"
          value={application}
          css={{ zIndex: theme.zindex.dropdown }}
          options={applications
            .filter(({ environments }) => isNotEmpty(environments))
            .map(({ id, name }) => ({
              value: id,
              label: name,
            }))}
          onChange={onSelectApplication}
          onClear={() => setApplication("")}
        />
      </Fieldset>
      <When condition={isNotEmpty(application)}>
        <Label required name="Select Environment" />
        <Container style={{ padding: 0 }}>
          {appEnvs.map((item) => (
            <Radio
              key={`radio.button.${item.nid}`}
              id={`radio.button.${item.nid}`}
              name="radiogroup"
              labelText={item.name}
              value={item.nid}
              checked={environmentId === item.nid}
              style={{ position: "relative" }}
              onChange={handleOptionChange}
            />
          ))}
        </Container>
      </When>
      <Button disabled={isDisabled} type="submit">
        Attach Resource
      </Button>
    </form>
  );
}
